"use client";

import theme, { blueGradientSxUp } from "@/theme/theme";
import { Stack, Typography, useMediaQuery } from "@mui/material";
import Image from "next/image";
import dynamic from "next/dynamic";
import SideTabbedButton from "@/components/generic/SideTabbedButton";

import servicesHeroAsset from "@/assets/images/servicesPage/servicesHeroAsset.webp";
import arrow from "@/assets/images/icons/arrow.webp";

// three.js is loaded client-side only, keeping it out of the SSR bundle.
const WaveField = dynamic(
    () => import("@/components/wave-field-v2").then((m) => m.WaveField),
    { ssr: false },
);

const QrHero = () => {
    // Mobile gets its own camera distance + lighter side fade (WaveField props
    // can't take MUI responsive objects, so we branch here).
    const isMobile = useMediaQuery(theme.breakpoints.down("lg"));

    return (
        <Stack
            alignItems="center"
            justifyContent="center"
            width="100%"
            minHeight={{ xs: "30vh", lg: "45vh" }}
            paddingX={4}
            position="relative"
            overflow="hidden"
        >
            <Image src={servicesHeroAsset} alt="quantum readiness hero asset" width={350} height={350} style={{
                position: "absolute",
                top: "-20%",
                right: "0%",
                zIndex: 0,
                opacity: 1,
                height: "auto"
            }} />
            <Stack width={{ xs: "100%", lg: "55%" }} alignItems="center" justifyContent="center" gap={4} marginX="auto" position="relative" sx={{ zIndex: 1, pt: { xs: "24px", lg: "48px" } }}>
                <Typography variant="h1" component="h1" fontSize={{ xs: "2rem !important", lg: "3.75rem !important" }} className="chno" fontWeight={500} textAlign="center" textTransform="uppercase" sx={blueGradientSxUp(theme)}>
                    Quantum Readiness
                </Typography>
                <Stack gap={2}>
                    <Typography
                        variant="h6"
                        component="h6"
                        fontWeight={300}
                        textAlign="center"
                        color={theme.palette.text.secondary}
                        fontSize={{ xs: "1rem !important", lg: "1rem !important" }}
                    >
                        Quantum computers running Shor&apos;s algorithm will break the public-key cryptography in
                        use today: RSA, ECDSA and Diffie-Hellman. Symmetric encryption such as AES-256 holds up.
                    </Typography>
                    <Typography
                        variant="h6"
                        component="h6"
                        fontWeight={300}
                        textAlign="center"
                        color={theme.palette.text.secondary}
                        fontSize={{ xs: "1rem !important", lg: "1rem !important" }}
                    >
                        That puts asymmetric cryptography first in line, because it protects the keys that
                        everything else depends on. Find out where your organisation stands with a free scan.
                    </Typography>
                </Stack>
                <SideTabbedButton
                    paddingX={18}
                    hoverShiftX={1.5}
                    hoverShiftY={-1}
                    action={() => {
                        document.querySelector("#free-scan")?.scrollIntoView({ behavior: "smooth", block: "start" });
                    }}
                >
                    <div className="flex items-center justify-center gap-1">
                        <Typography component="span" variant="h6" marginRight={0.5}>
                            Request a free scan
                        </Typography>
                        <span className="arrow-clip">
                            <span className="arrow-slide">
                                <Image src={arrow} alt="arrow animation" width={20} height={20} style={{ display: "block" }} />
                            </span>
                        </span>
                    </div>
                </SideTabbedButton>
            </Stack>
            <Image src={servicesHeroAsset} alt="quantum readiness hero asset" width={350} height={350} style={{
                position: "absolute",
                top: "-20%",
                left: "0%",
                transform: "scaleX(-1)",
                opacity: 1,
                zIndex: 0,
            }} />
            <Stack
                height={{ xs: "340px", lg: "560px" }}
                position="relative"
                sx={{
                    zIndex: 1,
                    pointerEvents: "none",
                    // No upward pull, so the opaque canvas never overlaps the button.
                    mt: 0,
                    // Cancel the parent's horizontal padding (paddingX={4} = 32px) on
                    // mobile so the wave spans the full viewport width instead of being
                    // clipped by the padding. Desktop keeps the padded 100% width.
                    width: { xs: "100vw", lg: "100%" },
                    mx: { xs: -4, lg: 0 },
                    // Desktop: side fade (left/right edges dissolve into the bg).
                    // Mobile: NO side fade — the wave runs off the viewport edges —
                    // plus a bottom fade so it dissolves into the background downward.
                    maskImage: isMobile
                        ? "linear-gradient(to bottom, #000 0%, #000 55%, rgba(0,0,0,0.15) 85%, rgba(0,0,0,0) 100%)"
                        : "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 8%, rgba(0,0,0,0.15) 22%, #000 38%, #000 62%, rgba(0,0,0,0.15) 78%, rgba(0,0,0,0) 92%, rgba(0,0,0,0) 100%)",
                    WebkitMaskImage: isMobile
                        ? "linear-gradient(to bottom, #000 0%, #000 55%, rgba(0,0,0,0.15) 85%, rgba(0,0,0,0) 100%)"
                        : "linear-gradient(to right, rgba(0,0,0,0) 0%, rgba(0,0,0,0) 8%, rgba(0,0,0,0.15) 22%, #000 38%, #000 62%, rgba(0,0,0,0.15) 78%, rgba(0,0,0,0) 92%, rgba(0,0,0,0) 100%)",
                }}
            >
                <WaveField
                    intro="settleSoft"
                    background="#0B0B0B"
                    colorA="#4d7cff"
                    colorB="#52d4e5"
                    shape={1}
                    seed={12.5}
                    amplitude={7}
                    frequency={0.11}
                    speed={0.4}
                    dotSize={1.2}
                    crestGlow={0.45}
                    density={150}
                    planeWidth={146}
                    planeDepth={130}
                    offsetX={0}
                    offsetY={15}
                    camHeight={40}
                    camDistance={isMobile ? 88 : 95}
                    camLookZ={5}
                    fov={57}
                    fadeStart={0}
                    fadeEnd={326}
                />
            </Stack>
        </Stack>
    );
};

export default QrHero;
